import _ from "underscore";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import analytics from "web/script/analytics/analytics";
import history from "web/script/utils/history";
import FullscreenGallery from "web/script/components/fullscreen-gallery";

import "web/script/components/slide-control";
import "web/script/components/gallery-thumbnails";
import "web/script/components/image-loader";

/**
 * This is the product image gallery. Unfortunately, it leads a triple life.
 *
 * On desktop, it will be a big image with selectable thumbnails.
 * On desktop (carousel), it will a carousel of selectable images.
 * On mobile, it will be a swipable gallery.
 *
 * This handles the functionality between the two. They share a fullscreen gallery, which
 * is largely why both forms exist in here.
 */

export default baustein.register("image-gallery", {
    defaultOptions: {
        images: null,
        retailer: null,
        retailer_name: null,
        designer: null,
        designerLink: null,
    },

    _firstInteraction: true,
    selectedIndex: 0,
    _displayedSlides: null,
    mobileSlideControl: null,
    oosSlideControl: null,
    fullscreenSlideControl: null,
    fullscreenOpenedByUser: false,
    preventMainImageChange: false,
    interactionCount: 0,

    setupEvents(add) {
        // desktop
        add("click", ".image-gallery-main-img-link", this.openFullscreenGalleryLinkClick);
        add("click", ".image-gallery__carousel__button", this._slideRight);
        add("thumbnailClick", "gallery-thumbnails", this._onThumbnailClick);
        add("thumbnailClick", "fullscreen-gallery", this._onThumbnailClick);
        add("mainImageLoaded", this._onMainImageLoaded);

        // mobile
        add("currentSlideChange", "slide-control", this._onSlideChange);
        add("slideClick", "slide-control", this._onSlideClick);
        add("indicatorClick", "slide-indicators", this._onIndicatorClick);

        // fullscreen
        add("overlayClose", "fullscreen-gallery", this._onFullscreenGalleryClose);
        add("viewSimilar", "fullscreen-gallery", this._onViewSimilar);
    },

    init() {
        if (!this.options.images) {
            this.destroy();
            return;
        }

        this._displayedSlides = [1];

        // There are now two `slide-control` components in the image gallery so we need to iterate over both
        this.findComponents("slide-control").forEach((component) => {
            component.addItem(
                this.options.images.map((image) => ({
                    type: "image",
                    full: image.large_url,
                    full2x: image.large_url_2x,
                    full_size_url: image.full_size_url,
                }))
            );

            /**
             * Here we find and set the mobile and out-of-stock `slide-control` components to variables
             * so we can easily apply different behaviours to each one.
             */
            if (component.options.isMobile) {
                this.mobileSlideControl = component;
            } else {
                this.oosSlideControl = component;
            }
        });
    },

    onInsert() {
        this.setGlobalHandler("resize", this._onResize);
    },

    onRemove() {
        this.releaseGlobalHandler("resize", this._onResize);
    },

    /**
     * For desktop out-of-stock.
     *
     * Slides the carousel right to the next image.
     */
    _slideRight() {
        this.oosSlideControl.next();
    },

    /**
     * For desktop.
     *
     * When you click a link for the fullscreen gallery, open it
     */
    openFullscreenGalleryLinkClick() {
        if (this.options.leadUrl) {
            window.open(this.options.leadUrl + "?reason=save-for-later-gallery");
        }

        if (history.supported) {
            var queryParams = history.getCurrentQueryParams();
            queryParams.set("product_gallery", this.options.productId);
            history.updateQuery(queryParams);
            this.fullscreenOpenedByUser = true;
        } else {
            this.openFullscreenGallery();
        }
    },

    /**
     * For desktop.
     *
     * When you click on a thumbnail, load that image in the main image spot.
     */
    _onThumbnailClick(event) {
        var imageIndex = event.index;
        this.selectedIndex = imageIndex;
        this.findComponent("gallery-thumbnails").setSelectedIndex(imageIndex, true);

        var image = this.options.images[imageIndex];

        if (!this.options.preventMainImageChange) {
            this.findComponent("image-loader").loadImage(image.large_url, image.large_url_2x);
        }

        // Set the index on slide-control to trigger cascade of events which will send analytics ..
        const slideControl = this.findComponent("slide-control");
        if (slideControl) {
            slideControl._setSelectedIndex(imageIndex);
        }
    },

    /**
     * For desktop.
     *
     * Runs after clicking a thumbnail and loading the image.
     */
    _onMainImageLoaded() {
        this.findComponent("gallery-thumbnails").setSelectedIndex(this.selectedIndex, false);
    },

    /**
     * For desktop.
     *
     * When the window is resized, force a refresh on the main image spot.
     */
    _onResize() {
        var loader = this.findComponent("image-loader");
        if (!loader) {
            return;
        }
        this.findComponent("image-loader").refresh();
    },

    /**
     * For mobile and desktop.
     *
     * When the slide changes, update the selected index, and...
     * - add the displayed slide to an array, and when the array equals the length of
     *   all items, then fire an event that all images have been seen
     * - if this is the first time the user has changed the slide in this gallery,
     *   fire an event indicating this
     * @param e
     * @private
     */
    _onSlideChange(e) {
        const analyticsAction = this._getAnalyticsAction();
        this.selectedIndex = e.index;

        if (this.options.swipeRedirect && this.interactionCount != null) {
            this.interactionCount += 1;
        }

        if (!e.fromIndicator) {
            // update all slide indicators if the event didn't come from one of them
            this.findComponents("slide-indicators").forEach((slideIndicator) => {
                slideIndicator.setSelectedIndex(e.index);
            });
        }

        if (!this._displayedSlides[e.index]) {
            this._displayedSlides.push(e.index);

            if (
                this._displayedSlides.length === this.mobileSlideControl.getItemCount() &&
                !this._seenAllSlides
            ) {
                analytics.event("engagement", analyticsAction, "seen_all");
                this._seenAllSlides = true;
            }
        }

        if (this._firstInteraction) {
            analytics.event("engagement", analyticsAction, "interacted");
            this._firstInteraction = false;
        }
        // To prevent changed_image when gallery opened
        if (!_.isNull(e.oldIndex) && e.oldIndex != e.index) {
            analytics.event("engagement", analyticsAction, "changed_image");
        }
        // pmt_221 - after swiping twice we load the partner page, this is a onetime action
        if (this.interactionCount >= 2) {
            this.interactionCount = null;
            analytics.event("engagement", "redirect_on_swipe");
            window.open(
                "/track/lead/" + this.options.currentLinkId + "?reason=redirect_on_swipe",
                "_blank"
            );
        }
    },

    _getAnalyticsAction() {
        const viewport = browser.getViewport() === browser.VIEWPORTS.DESKTOP ? "desktop" : "mobile";
        return `${viewport}_gallery`;
    },

    /**
     * For mobile.
     *
     * When you click on a slide, if it's the main image, load the fullscreen gallery. If
     * it isn't, then move over to that slide.
     * @param e
     * @private
     */
    _onSlideClick(e) {
        if (e.target.options.isMobile) {
            if (e.index === e.target.getCurrentIndex()) {
                this.openFullscreenGalleryLinkClick();
                return;
            }
            this.mobileSlideControl.goToSlide(e.index);
        } else {
            this.selectedIndex = e.index;
            this.openFullscreenGalleryLinkClick();
        }
    },

    /**
     * For mobile.
     *
     * When you click on an indicator, go to that slide.
     * @param e
     * @private
     */
    _onIndicatorClick(e) {
        const currentSlideControl = this.fullscreenSlideControl || this.mobileSlideControl;
        currentSlideControl.goToSlide(e.newIndex, true);
    },

    /**
     * Creates an instance of the FullscreenGallery component, appends it to this component and then returns it.
     * @returns {Component}
     */
    openFullscreenGallery() {
        const analyticsAction = this._getAnalyticsAction();
        analytics.event("engagement", analyticsAction, "open_fullscreen");
        var gallery = this.findComponent("fullscreen-gallery");

        if (gallery) {
            return gallery;
        }

        gallery = new FullscreenGallery({
            items: this.options.images.map(function(image) {
                var imageDefinition = _.clone(image);
                imageDefinition.type = "image";
                return imageDefinition;
            }),
            selectedIndex: this.selectedIndex,
            zoomEnabled: true,
            retailer: this.options.retailer,
            designer: this.options.designer,
            designerLink: this.options.designerLink,
            viewSimilarEnabled: this.options.viewSimilarEnabled,
        });

        gallery.appendTo(this);

        this.fullscreenSlideControl = gallery.findComponent("slide-control");

        return gallery;
    },

    /**
     * Closes the fullscreen gallery by finding the component and destroying it.
     */
    closeFullscreenGallery() {
        var gallery = this.findComponent("fullscreen-gallery");

        if (gallery) {
            gallery.destroy();
        }
    },

    /**
     * When the user tries to close the gallery, this handles the close event emitted by the
     * fullscreen gallery.
     *
     * This is only every called when the user uses the gallery UI to close the gallery, not if
     * the user presses back or forward in the browser.
     *
     * If the fullscreen gallery was invoked by the user clicking on something, then it goes
     * back in the history. Otherwise, it will update the history params to remove it.
     * @private
     */
    _onFullscreenGalleryClose() {
        if (history.supported) {
            if (this.fullscreenOpenedByUser) {
                history.back();
            } else {
                // If this is the first page in the history stack, go forward in time so to to speak.
                // Our _onQueryParameterChange will handle this from here on.
                var queryParams = history.getCurrentQueryParams();
                queryParams.delete("product_gallery");
                history.updateQuery(queryParams, true);
            }
        }

        this.closeFullscreenGallery();
        this.fullscreenSlideControl = null;
    },

    /**
     * When the user tries to view related products from the fullscreen gallery, need to close it
     * and remove its query param from the URL
     */
    _onViewSimilar() {
        this.closeFullscreenGallery();
        let queryParams = history.getCurrentQueryParams();
        queryParams.delete("product_gallery");
        history.updateQuery(queryParams, true);
    },
});
